import { useEffect, useState } from 'react';
import { deleteMetaPage, fetchMetaPages, toggleMetaPage, upsertMetaPage } from '../api';
import type { MetaPage } from '../types';
import IvrConfigsPanel from './IvrConfigsPanel';

type Section = 'meta_pages' | 'ivr_configs';

export default function AdminSheet({ onClose }: { onClose: () => void }) {
  const [section, setSection] = useState<Section>('meta_pages');
  const [pages, setPages] = useState<MetaPage[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // add-form state
  const [pageId, setPageId] = useState('');
  const [pageName, setPageName] = useState('');
  const [token, setToken] = useState('');

  async function load() {
    setLoading(true);
    try {
      const d = await fetchMetaPages();
      setPages(d.pages);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'load failed');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function add() {
    if (!pageId.trim() || !token.trim()) return;
    setBusy(true);
    try {
      const r = await upsertMetaPage(pageId.trim(), token.trim(), pageName.trim() || undefined);
      setPageId('');
      setPageName('');
      setToken('');
      await load();
      alert(`Added ${r.name ?? r.page_id}`);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'add failed');
    } finally {
      setBusy(false);
    }
  }

  async function toggle(p: MetaPage) {
    setBusy(true);
    try {
      await toggleMetaPage(p.page_id, !p.active);
      await load();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'toggle failed');
    } finally {
      setBusy(false);
    }
  }

  async function remove(p: MetaPage) {
    if (!confirm(`Remove ${p.name ?? p.page_id}? Meta webhooks for this page will fail.`)) return;
    setBusy(true);
    try {
      await deleteMetaPage(p.page_id);
      await load();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'delete failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="absolute inset-x-0 bottom-0 max-h-[92vh] overflow-hidden rounded-t-3xl bg-white shadow-2xl"
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 className="text-base font-semibold">Admin</h2>
          <button onClick={onClose} className="text-sm text-slate-500">
            Close
          </button>
        </div>

        <div className="flex gap-1.5 border-b border-slate-100 px-4 py-2">
          <button
            onClick={() => setSection('meta_pages')}
            className={`rounded-full px-3 py-1 text-xs font-medium ${
              section === 'meta_pages'
                ? 'bg-wechat-accent text-white'
                : 'bg-white text-slate-600 border border-slate-200'
            }`}
          >
            Meta pages
          </button>
          <button
            onClick={() => setSection('ivr_configs')}
            className={`rounded-full px-3 py-1 text-xs font-medium ${
              section === 'ivr_configs'
                ? 'bg-wechat-accent text-white'
                : 'bg-white text-slate-600 border border-slate-200'
            }`}
          >
            IVR configs
          </button>
        </div>

        <div className="max-h-[78vh] space-y-4 overflow-y-auto px-4 py-4 pb-[env(safe-area-inset-bottom,16px)]">
          {section === 'ivr_configs' && <IvrConfigsPanel />}
          {section === 'meta_pages' && <>
          {err && <p className="rounded-lg bg-red-50 p-2 text-xs text-red-700">{err}</p>}

          <section>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
              Connected pages ({pages.length})
            </h3>
            {loading && <p className="text-sm text-slate-500">Loading…</p>}
            {!loading && pages.length === 0 && (
              <p className="text-sm text-slate-500">No pages yet. Add one below.</p>
            )}
            <ul className="space-y-2">
              {pages.map((p) => (
                <li
                  key={p.page_id}
                  className={`rounded-xl border p-3 ${
                    p.active ? 'border-emerald-200 bg-emerald-50/40' : 'border-slate-200 bg-slate-50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-slate-900">
                          {p.name ?? 'Unnamed page'}
                        </span>
                        <span
                          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                            p.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'
                          }`}
                        >
                          {p.active ? 'active' : 'paused'}
                        </span>
                      </div>
                      <div className="mt-0.5 font-mono text-[11px] text-slate-500">
                        {p.page_id}
                      </div>
                      <div className="mt-1 text-[11px] text-slate-500">
                        Token {p.token_preview}
                      </div>
                      {p.last_used && (
                        <div className="text-[11px] text-slate-500">
                          Last used · {p.last_used}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-1">
                      <button
                        disabled={busy}
                        onClick={() => toggle(p)}
                        className="rounded-full bg-slate-200 px-2.5 py-1 text-[10px] font-semibold text-slate-700 disabled:opacity-50"
                      >
                        {p.active ? 'Pause' : 'Activate'}
                      </button>
                      <button
                        disabled={busy}
                        onClick={() => remove(p)}
                        className="rounded-full bg-red-100 px-2.5 py-1 text-[10px] font-semibold text-red-700 disabled:opacity-50"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          <section className="rounded-xl border border-slate-200 bg-white p-3">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
              Add / update a page
            </h3>
            <p className="mb-2 text-[11px] text-slate-500">
              Get the page ID from Meta Business Settings → Pages. Get a page access token from Graph
              API Explorer with <code className="rounded bg-slate-100 px-1">leads_retrieval</code> +{' '}
              <code className="rounded bg-slate-100 px-1">pages_show_list</code> scopes. The worker
              validates the token against Meta before saving.
            </p>
            <input
              value={pageId}
              onChange={(e) => setPageId(e.target.value)}
              placeholder="Page ID (e.g. 543385279186453)"
              className="mb-2 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-xs"
            />
            <input
              value={pageName}
              onChange={(e) => setPageName(e.target.value)}
              placeholder="Display name (optional — auto-resolved from Meta)"
              className="mb-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
            <textarea
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Page access token (starts with EAA…)"
              rows={3}
              className="mb-2 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-[11px]"
            />
            <button
              disabled={busy || !pageId.trim() || !token.trim()}
              onClick={add}
              className="w-full rounded-lg bg-wechat-accent px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              {busy ? 'Saving…' : 'Save page'}
            </button>
          </section>
          </>}
        </div>
      </div>
    </div>
  );
}
